import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { compose } from "recompose";
import { connect, useDispatch } from "react-redux";
import { withTranslation } from "react-i18next";
import {
  Route,
  Switch,
  Redirect,
  useParams,
  useLocation
} from "react-router-dom";
import * as R from "ramda";

import * as gtag from "../../../../lib/gtag";
import Page from "../../../../components/display/Page";
import Nav from "../../../../components/Nav";
import {
  listBatchAction,
  listBatchInvite,
  candidatesBatchShare,
  multipleSelectCandidate,
  resetMultipleSelectCandidates,
  updateCandidateStatus,
  fetchCandidatesWithPagination,
  fetchCandidatesWithoutPagination
} from "../../../../store/actions/candidates";
import { resetOpportunityNotifications } from "../../../../store/actions/opportunity";
import {
  CANDIDATE_LISTS,
  CANDIDATE_LISTS_URLS,
  FILTERS_FOR
} from "../../../../constants/opportunity";
import { filterCandidatesThatAreChangingLists } from "../../../../utils/candidates";
import { getFiltersForList } from "../../../../utils/candidatesFilters";
import api from "../../../../utils/api";

import CandidatesSection from "./CandidatesSection";
import { DEFAULT_SERVER_FILTERS_FOR_OPPORTUNITY } from "./filters/useFilters";

import styled from "styled-components/macro";

const Content = styled.div`
  margin: 24px;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  border-bottom: 24px solid #fff;
`;

const NavStyled = styled(Nav)`
  &.ant-menu {
    width: 100%;
  }

  .ant-menu-item {
    border: 1px solid #e5e9ec;
    color: black;
  }

  &.ant-menu-horizontal > .ant-menu-overflowed-submenu {
    display: none !important;
  }

  &.ant-menu-horizontal {
    background: #f1f2f5;
    display: flex;
    justify-content: stretch;
    overflow-x: hidden;
  }

  &.ant-menu-horizontal > .ant-menu-item {
    flex: 1 1 0;
    float: none;
    width: auto;
    min-width: 0;
    margin-top: 0;
    margin-bottom: 0;
    display: flex;
    justify-content: space-between;
    border-top: 0px;
    border-bottom: 0px;

    > a {
      align-items: center;
      display: inline-flex;
      gap: 8px;
      justify-content: center;
      white-space: nowrap;
    }

    > span {
      display: none;
    }

    &:hover {
      border-top: 1px solid ${({ theme }) => theme["@primary"]};
      border-bottom: 0px;
    }
  }

  &.ant-menu-horizontal > .ant-menu-item-selected {
    border-top: 1px solid ${({ theme }) => theme["@primary"]};
    border-bottom: 0px;
    background: white;

    &:hover {
      background: white;
    }
  }
`;

const getFilters = ({ opportunity, list, searchQuery }) => ({
  [CANDIDATE_LISTS.suggested]: getFiltersForList({
    opportunity: opportunity,
    filterKey: `${opportunity.id}_${CANDIDATE_LISTS.suggested}`,
    filtersUsed: FILTERS_FOR[CANDIDATE_LISTS.suggested],
    searchQuery: list === CANDIDATE_LISTS.suggested ? searchQuery : "",
    onlyServerFilters: true
  }),
  [CANDIDATE_LISTS.hunting]: getFiltersForList({
    opportunity: opportunity.selected,
    filterKey: `${opportunity.id}_${CANDIDATE_LISTS.hunting}`,
    filtersUsed: FILTERS_FOR[CANDIDATE_LISTS.hunting],
    searchQuery: list === CANDIDATE_LISTS.hunting ? searchQuery : "",
    onlyServerFilters: true
  })
});

const RenderCandidatesSection = ({
  list,
  description,
  loadingStatus,
  candidates,
  fetch,
  // params, // used in lazy/always loading
  ...sectionProps
}) => {
  // Lazy loading
  // const [loaded, setLoaded] = useState({});
  //
  // useEffect(() => {
  //   if (loaded[list]) return;
  //   fetch({
  //     filters: getFilters(params)[list]
  //   });
  //   setLoaded(loaded => ({
  //     ...loaded,
  //     [list]: true
  //   }));
  // }, [fetch, list, loaded, params]);

  // Always loading
  // useEffect(() => {
  //   fetch({
  //     filters: getFilters(params)[list]
  //   });
  // }, [fetch, list, params]);

  return (
    <CandidatesSection
      key={list} // Add key to make sure that useFilters hook restarts
      list={list}
      loading={loadingStatus[list]}
      description={description}
      candidatesData={candidates}
      onServerFiltersChange={fetch}
      {...sectionProps}
    />
  );
};

const OpportunityCandidates = ({
  t,
  canMove,
  canChat,
  restrictAdvanceToProcessByScreening,
  match: routeMatch,
  history,
  opportunity,
  suggestedCandidates,
  huntingCandidates,
  selectedCandidates,
  rejectedCandidates,
  multipleSelectCandidate,
  resetMultipleSelectCandidates,
  updateCandidateStatus,
  fetchCandidatesWithPagination,
  fetchCandidatesWithoutPagination,
  multipleSelectedCandidates,
  listBatchAction,
  listBatchInvite,
  candidatesBatchShare,
  userConfig
}) => {
  const dispatch = useDispatch();
  const params = useParams();
  const location = useLocation();

  useEffect(() => {
    gtag.event({
      category: "VacancyCandidates",
      action: `VacancyCandidates`,
      label: opportunity.id
    });
  }, [opportunity.id]);

  const rootUrl = `/oportunidades/${opportunity.id}/candidatos`;

  // Use ref to prevent adding params in useEffect dependencies
  const fetchCandidatesParams = useRef({
    opportunity,
    list: R.pipe(
      R.toPairs,
      R.find(([, urlPath]) => urlPath === params.category),
      ([list] = []) => list
    )(CANDIDATE_LISTS_URLS),
    searchQuery: location.search
  });

  const fetchSuggested = useCallback(
    ({ filters: serverFilters }) =>
      fetchCandidatesWithPagination({
        filters: serverFilters,
        list: CANDIDATE_LISTS.suggested,
        endpoint: api.opportunity.candidates.fetchSuggested
      }),
    [fetchCandidatesWithPagination]
  );

  const fetchHunting = useCallback(
    ({ filters: serverFilters }) =>
      fetchCandidatesWithPagination({
        filters: serverFilters,
        list: CANDIDATE_LISTS.hunting,
        endpoint: api.opportunity.candidates.fetchHunting
      }),
    [fetchCandidatesWithPagination]
  );

  const fetchSelected = useCallback(
    () =>
      fetchCandidatesWithoutPagination({
        list: CANDIDATE_LISTS.selected,
        endpoint: api.opportunity.candidates.fetchSelected
      }),
    [fetchCandidatesWithoutPagination]
  );

  const fetchRejected = useCallback(
    () =>
      fetchCandidatesWithoutPagination({
        list: CANDIDATE_LISTS.rejected,
        endpoint: api.opportunity.candidates.fetchRejected
      }),
    [fetchCandidatesWithoutPagination]
  );

  useEffect(() => {
    // Pre loading
    fetchSuggested({
      filters: getFilters(fetchCandidatesParams.current)[
        CANDIDATE_LISTS.suggested
      ]
    });
    fetchHunting({
      filters: getFilters(fetchCandidatesParams.current)[
        CANDIDATE_LISTS.hunting
      ]
    });
    fetchSelected();
    fetchRejected();
    dispatch(
      resetOpportunityNotifications({
        id: opportunity.id,
        type: "like"
      })
    );
  }, [
    dispatch,
    fetchCandidatesWithPagination,
    fetchHunting,
    fetchRejected,
    fetchSelected,
    fetchSuggested,
    opportunity.id
  ]);

  useEffect(() => {
    const handleWindowFocus = () => {
      if (params.category === CANDIDATE_LISTS_URLS[CANDIDATE_LISTS.selected]) {
        fetchSelected();
      }
    };

    window.addEventListener("focus", handleWindowFocus);

    return () => {
      window.removeEventListener("focus", handleWindowFocus);
    };
  }, [fetchSelected, params.category]);

  const candidatesSectionProps = {
    listBatchAction,
    listBatchInvite,
    candidatesBatchShare,
    canMove,
    canChat,
    restrictAdvanceToProcessByScreening,
    opportunity,
    updateCandidateStatus,
    history,
    routeMatch,
    selectedCandidatesForOpportunity: R.propOr(
      [],
      "candidates",
      selectedCandidates
    ),
    selectedCandidatesForOpportunityUnavailable:
      R.propOr(true, "loading", selectedCandidates) ||
      R.propOr(false, "rejected", selectedCandidates),
    multipleSelectCandidate,
    multipleSelectedCandidates,
    resetMultipleSelectCandidates
  };

  useEffect(() => {
    return resetMultipleSelectCandidates;
  }, [resetMultipleSelectCandidates]);

  const listLoadingStatus = useMemo(
    () => ({
      [CANDIDATE_LISTS.suggested]: R.prop("loading", suggestedCandidates),
      [CANDIDATE_LISTS.hunting]: R.prop("loading", huntingCandidates),
      [CANDIDATE_LISTS.selected]: R.prop("loading", selectedCandidates),
      [CANDIDATE_LISTS.rejected]: R.prop("loading", rejectedCandidates)
    }),
    [
      huntingCandidates,
      rejectedCandidates,
      selectedCandidates,
      suggestedCandidates
    ]
  );

  return (
    <Page>
      <Content>
        <Header>
          <NavStyled
            onClick={resetMultipleSelectCandidates}
            zIndex={2}
            path={routeMatch.url}
            style={{
              borderBottom: 0
            }}
            links={R.filter(R.identity, [
              {
                testKey: "sugeridos",
                to: `${rootUrl}/${
                  CANDIDATE_LISTS_URLS[CANDIDATE_LISTS.suggested]
                }`,
                icon: "bulb",
                label: t("opportunity:candidates.suggestedCandidatesTitle")
                // loading: listLoadingStatus[CANDIDATE_LISTS.suggested]
                // count: suggestedCandidates.length || 0,
              },
              R.pathOr(true, ["hunting"], userConfig) && {
                testKey: "buscar",
                to: `${rootUrl}/${
                  CANDIDATE_LISTS_URLS[CANDIDATE_LISTS.hunting]
                }`,
                icon: "search",
                label: t("opportunity:candidates.huntingCandidatesTitle")
                // loading: listLoadingStatus[CANDIDATE_LISTS.hunting]
                // count: huntingCandidates.length || 0,
              },
              {
                testKey: "seleccionados",
                to: `${rootUrl}/${
                  CANDIDATE_LISTS_URLS[CANDIDATE_LISTS.selected]
                }`,
                icon: "check-circle",
                label: t("opportunity:candidates.selectedCandidatesTitle")
                // loading: listLoadingStatus[CANDIDATE_LISTS.selected]
                // count: selectedCandidates.length || 0,
              },
              {
                testKey: "rechazados",
                to: `${rootUrl}/${
                  CANDIDATE_LISTS_URLS[CANDIDATE_LISTS.rejected]
                }`,
                icon: "minus-circle",
                label: t("opportunity:candidates.rejectedCandidatesTitle")
                // loading: listLoadingStatus[CANDIDATE_LISTS.rejected]
                // count: rejectedCandidates.length || 0,
              }
            ])}
          />
        </Header>
        <Switch>
          <Route
            path={`${rootUrl}/${
              CANDIDATE_LISTS_URLS[CANDIDATE_LISTS.suggested]
            }`}
            render={() => (
              <RenderCandidatesSection
                list={CANDIDATE_LISTS.suggested}
                description={t("opportunity:candidates.suggestedDescription")}
                params={fetchCandidatesParams.current}
                loadingStatus={listLoadingStatus}
                candidates={suggestedCandidates}
                fetch={fetchSuggested}
                {...candidatesSectionProps}
              />
            )}
          />
          {R.pathOr(true, ["hunting"], userConfig) && (
            <Route
              path={`${rootUrl}/${
                CANDIDATE_LISTS_URLS[CANDIDATE_LISTS.hunting]
              }`}
              render={() => (
                <RenderCandidatesSection
                  list={CANDIDATE_LISTS.hunting}
                  description={t(
                    "opportunity:candidates.searchCandidatesDescription"
                  )}
                  params={fetchCandidatesParams.current}
                  loadingStatus={listLoadingStatus}
                  candidates={huntingCandidates}
                  fetch={fetchHunting}
                  {...candidatesSectionProps}
                />
              )}
            />
          )}
          <Route
            path={`${rootUrl}/${
              CANDIDATE_LISTS_URLS[CANDIDATE_LISTS.selected]
            }`}
            render={() => (
              <RenderCandidatesSection
                list={CANDIDATE_LISTS.selected}
                description={t(
                  "opportunity:candidates.selectedCandidatesDescription"
                )}
                params={fetchCandidatesParams.current}
                loadingStatus={listLoadingStatus}
                candidates={selectedCandidates}
                fetch={fetchSelected}
                {...candidatesSectionProps}
                serverFiltersList={R.reject(
                  R.equals("registered"),
                  DEFAULT_SERVER_FILTERS_FOR_OPPORTUNITY
                )}
              />
            )}
          />
          <Route
            path={`${rootUrl}/${
              CANDIDATE_LISTS_URLS[CANDIDATE_LISTS.rejected]
            }`}
            render={() => (
              <RenderCandidatesSection
                list={CANDIDATE_LISTS.rejected}
                description={t(
                  "opportunity:candidates.rejectedCandidatesDescription"
                )}
                params={fetchCandidatesParams.current}
                loadingStatus={listLoadingStatus}
                candidates={rejectedCandidates}
                fetch={fetchRejected}
                {...candidatesSectionProps}
                multipleSelectCandidate={false}
                serverFiltersList={R.reject(
                  R.equals("registered"),
                  DEFAULT_SERVER_FILTERS_FOR_OPPORTUNITY
                )}
              />
            )}
          />
          <Redirect
            to={`${rootUrl}/${CANDIDATE_LISTS_URLS[CANDIDATE_LISTS.suggested]}`}
          />
        </Switch>
      </Content>
    </Page>
  );
};

const enhancer = compose(
  withTranslation(["global", "opportunity", "candidates"]),
  connect(
    ({ company, candidates, opportunity, user }) => ({
      company,
      stats: candidates.stats,
      opportunitySelected: opportunity.selected,
      suggestedCandidates: filterCandidatesThatAreChangingLists(
        candidates.lists[CANDIDATE_LISTS.suggested]
      ),
      huntingCandidates: filterCandidatesThatAreChangingLists(
        candidates.lists[CANDIDATE_LISTS.hunting]
      ),
      selectedCandidates: filterCandidatesThatAreChangingLists(
        candidates.lists[CANDIDATE_LISTS.selected]
      ),
      rejectedCandidates: filterCandidatesThatAreChangingLists(
        candidates.lists[CANDIDATE_LISTS.rejected]
      ),
      multipleSelectedCandidates: candidates.multipleSelectedCandidates,
      userConfig: R.propOr({}, ["config"], user)
    }),
    {
      listBatchAction,
      listBatchInvite,
      candidatesBatchShare,
      multipleSelectCandidate,
      resetMultipleSelectCandidates,
      updateCandidateStatus,
      fetchCandidatesWithPagination,
      fetchCandidatesWithoutPagination
    }
  )
);

export default enhancer(OpportunityCandidates);
